import React, { ReactElement, useEffect, useState } from 'react'
import { usePrevious } from 'react-use'

import { addAlert, addAlertSignature } from '../actions/alerts'
import { acceptUserInvite, logout } from '../actions/user'
import { getAcceptUserInvite } from '../api/user'
import Alert from '../components/elements/alert'
import Button from '../components/elements/button'
import Card from '../components/elements/card'
import Checkbox from '../components/elements/checkbox'
import Modal from '../components/elements/modal'
import Title from '../components/elements/Title'
import DumbLink from '../components/widgets/DumbLink'
import LoadingOverlay from '../components/widgets/LoadingOverlay'
import UserTerms from '../components/widgets/UserTerms'
import paths from '../constants/paths'
import { CompanyGroupUser } from '../model/companyGroup'
import CompanyUser from '../model/companyUser'
import { UserReducer } from '../reducers/user'
import { formatError, isRequestError } from '../utils/error-utils'
import { connectToReducer } from '../utils/reducer-utils'
import { RequestError } from '../utils/request-utils'
import { RouteProps, splatString } from '../utils/route-utils'
import { t, tx } from '../utils/translation-utils'

type Reducers = {
  user: UserReducer
}

type Actions = {
  addAlert: addAlertSignature
  acceptUserInvite: (token: string) => Promise<(CompanyUser & CompanyGroupUser) | RequestError | void>
  logout: () => Promise<boolean | void>
}

type State = {
  email: string
  company?: {
    name: string
    nationalID: string
  }
  companyGroup?: {
    name: string
  }
}

function AcceptUserInvite(props: Reducers & Actions & RouteProps): ReactElement | null {
  const { user, addAlert } = props
  const previousUser = usePrevious(user)
  const tokenID = splatString(props.params.splat)
  const [loadingInfo, setLoadingInfo] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [info, setInfo] = useState<State>()
  const [error, setError] = useState<Error>()
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [showTerms, setShowTerms] = useState(false)

  useEffect(() => {
    if (previousUser && previousUser.acceptingUserInvite && !user.acceptingUserInvite) {
      if (user.error) {
        addAlert('warning', t('accept_user_invite.warning.broken_token'))
      }
    }
  })

  useEffect(() => {
    if (!info && !loadingInfo && !error) {
      setLoadingInfo(true)
      getAcceptUserInvite(tokenID)
        .then((res) => {
          if (!res) {
            return
          }
          if (res.data.company) {
            setInfo({
              email: user.email ?? user.name ?? t('common.unknown'),
              company: {
                name: res.data.company.name,
                nationalID: res.data.company.nationalID,
              },
            })
          }
          if (res.data.companyGroup) {
            setInfo({
              email: user.email ?? user.name ?? t('common.unknown'),
              companyGroup: {
                name: res.data.companyGroup.name,
              },
            })
          }
        })
        .catch((e) => {
          if (isRequestError(e)) {
            setError(e)
          }
        })
        .finally(() => {
          setLoadingInfo(false)
        })
    }
  }, [info, loadingInfo, tokenID, user, error])

  if (accepting || !info) {
    if (error) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Card style={{ maxWidth: '500px', width: '100%' }}>
            <Alert type={'error'} message={formatError(error)} showIcon />
          </Card>
        </div>
      )
    }
    return (
      <div style={{ position: 'relative', minHeight: '300px', marginTop: '96px' }}>
        <LoadingOverlay />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Card style={{ maxWidth: '500px', width: '100%' }}>
        {error && <Alert type={'error'} message={formatError(error)} showIcon />}
        {info.company && (
          <>
            <Title>{t('accept_user_invite.company.title')}</Title>
            <p style={{ fontSize: '14px', lineHeight: '16px' }}>
              {tx('accept_user_invite.company.intro.line_1', {
                email: <strong>{info.email}</strong>,
                companyName: <strong>{info.company.name}</strong>,
                companyCVR: info.company.name,
              })}
            </p>
            <p style={{ fontSize: '14px', lineHeight: '16px' }}>{t('accept_user_invite.company.intro.line_2')}</p>
          </>
        )}
        {info.companyGroup && (
          <>
            <Title>{t('accept_user_invite.company_group.title')}</Title>
            <p style={{ fontSize: '14px', lineHeight: '16px' }}>
              {tx('accept_user_invite.company_group.intro.line_1', {
                email: <strong>{info.email}</strong>,
                name: <strong>{info.companyGroup.name}</strong>,
              })}
            </p>
            <p style={{ fontSize: '14px', lineHeight: '16px' }}>{t('accept_user_invite.company_group.intro.line_2')}</p>
          </>
        )}
        <Checkbox
          style={{ marginTop: '16px', marginBottom: '16px' }}
          id="checkbox-accept-terms"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
        >
          <span style={{ fontSize: '14px', paddingLeft: '5px' }}>
            {tx('register.form.terms', {
              terms: (
                <DumbLink
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault()
                    setShowTerms(true)
                  }}
                >
                  {t('register.form.terms.terms')}
                </DumbLink>
              ),
              privacy: (
                <a href="https://salary.dk/privacy-policy/" target="_blank" rel="noreferrer">
                  {t('register.form.terms.privacy')}
                </a>
              ),
            })}
          </span>
        </Checkbox>
        <Modal
          visible={showTerms}
          onOk={() => setShowTerms(false)}
          onCancel={() => setShowTerms(false)}
          width={579}
          footer={null}
        >
          <UserTerms setVisibility={setShowTerms} />
        </Modal>
        <div className="button-row">
          <Button
            onClick={(e) => {
              e.stopPropagation()
              setAccepting(true)
              props
                .acceptUserInvite(tokenID)
                .then((res) => {
                  if (!res || !(res instanceof RequestError)) {
                    // Redirect to dashboard, but ensure a hard refresh
                    document.location = '/'
                  }
                })
                .catch((e) => {
                  if (isRequestError(e)) {
                    setError(e)
                  }
                })
            }}
            type="primary"
            size="extra-large"
            disabled={!termsAccepted}
          >
            {t('accept_user_invite.button.yes')}
          </Button>
          <Button
            size="extra-large"
            onClick={() => {
              document.location = '/'
            }}
            danger
          >
            {t('accept_user_invite.button.no')}
          </Button>
          <Button
            onClick={() => {
              props
                .logout()
                .then((res) => {
                  if (res) {
                    document.location = '/' + paths.ACCEPT_USER_INVITE + '/' + tokenID
                  }
                })
                .catch((e) => {
                  setError(e)
                })
            }}
            type="secondary"
            size="extra-large"
          >
            {t('accept_user_invite.button.switch_user')}
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default connectToReducer<Reducers, Actions, RouteProps>(
  (state) => ({
    user: state.user,
  }),
  {
    addAlert: addAlert,
    acceptUserInvite: acceptUserInvite,
    logout: logout,
  }
)(AcceptUserInvite)
